import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  CROSS_CLIENT_MATCH_NONE,
  CrossClientQueryEvaluation,
  CrossClientQueryEvaluationResultStatuses,
  CrossClientQueryEvaluationStatus,
  CrossClientQueryEvaluationStatuses,
  CrossClientQueryEvaluationType,
  CrossClientQueryEvaluationTypes,
  CrossClientQueryExecution,
  CrossClientQueryMatchDistribution,
  CrossClientQueryResolverStage,
  CrossClientQueryResolverStages,
  CrossClientQueryResult,
  CrossClientQueryResultBucketCount,
  CrossClientQueryResultMatch,
  CrossClientQueryResultMatches,
  CrossClientQueryResultStatus,
  CrossClientQueryResultStatuses,
  CrossClientResultTab,
  CrossClientResultTabs,
} from '../../models/CrossClient'
import { BasePageResponse } from '../models'
import { PageMetadata } from '../../models/PageMetadata'
import { User } from '../../models/User'
import { selectCrossClientActionedClientIds } from './crossClientActions'
import { executionIsActive } from './utils'

interface CrossClientResultsSearch {
  clientName: string | null
}

interface CrossClientTabResults {
  pages: Record<number, CrossClientQueryResult[]>
  totalElements: number
  totalPages: number
}

interface CrossClientResultFilter {
  match?: (CrossClientQueryResultMatch | typeof CROSS_CLIENT_MATCH_NONE)[]
  status?: CrossClientQueryResultStatus
}

const EMPTY_RESULTS: CrossClientQueryResult[] = []
const EMPTY_EVALUATIONS: CrossClientQueryEvaluation[] = []
const EMPTY_BUCKETS: CrossClientQueryResultBucketCount[] = []

const TERMINAL_EVALUATION_STATUSES: CrossClientQueryEvaluationStatus[] = [
  CrossClientQueryEvaluationStatuses.COMPLETED,
  CrossClientQueryEvaluationStatuses.CANCELED,
  CrossClientQueryEvaluationStatuses.ERRORED,
]

const isTerminalEvaluationStatus = (status: CrossClientQueryEvaluationStatus): boolean => TERMINAL_EVALUATION_STATUSES.includes(status)

export const CROSS_CLIENT_DEFAULT_PAGE_SIZE = 20

export const tabToResultFilter = (tab: CrossClientResultTab): CrossClientResultFilter => {
  switch (tab) {
    case CrossClientResultTabs.MATCH:
      return { match: [CrossClientQueryResultMatches.MATCH] }
    case CrossClientResultTabs.POTENTIAL:
      return { match: [CrossClientQueryResultMatches.POTENTIAL], status: CrossClientQueryResultStatuses.INITIALIZED }
    case CrossClientResultTabs.REMAINING:
      return { match: [CROSS_CLIENT_MATCH_NONE], status: CrossClientQueryResultStatuses.INITIALIZED }
    case CrossClientResultTabs.NO_MATCH:
      return { match: [CrossClientQueryResultMatches.NO_MATCH] }
    case CrossClientResultTabs.MISSING_DATA:
      return { status: CrossClientQueryResultStatuses.NO_DATA }
  }
}

interface CrossClientResultsState {
  execution: CrossClientQueryExecution | null
  executionLoading: boolean
  selectedTab: CrossClientResultTab
  resultsByTab: Partial<Record<CrossClientResultTab, CrossClientTabResults>>
  matchDistribution: CrossClientQueryMatchDistribution | null
  resultBuckets: CrossClientQueryResultBucketCount[]
  activeEvaluations: CrossClientQueryEvaluation[]
  progressStatuses: Partial<Record<CrossClientQueryResultStatus, number>>
  search: CrossClientResultsSearch
  hasActions: boolean | null
  selectedResultIds: string[]
  clients: EntityState<User, string>
  fetchedClientIds: Record<string, true>
  teammates: User[]
  clientsLoading: boolean
  teammatesLoading: boolean
  pollingEnabled: boolean
  paging: { page: number; size: number }
}

const crossClientClientsAdapter = createEntityAdapter<User>()

const emptyPageMeta: PageMetadata = { totalElements: 0, totalPages: 0, number: 0 }

const initialState: CrossClientResultsState = {
  execution: null,
  executionLoading: true,
  selectedTab: CrossClientResultTabs.MATCH,
  resultsByTab: {},
  matchDistribution: null,
  resultBuckets: [],
  activeEvaluations: [],
  progressStatuses: {},
  search: { clientName: null },
  hasActions: null,
  selectedResultIds: [],
  clients: crossClientClientsAdapter.getInitialState(),
  fetchedClientIds: {},
  teammates: [],
  clientsLoading: true,
  teammatesLoading: true,
  pollingEnabled: true,
  paging: { page: 1, size: CROSS_CLIENT_DEFAULT_PAGE_SIZE },
}

const resetResultsForFilterChange = (state: CrossClientResultsState) => {
  state.paging.page = 1
  state.resultsByTab = {}
  state.selectedResultIds = []
}

const forEachCachedResult = (state: CrossClientResultsState, fn: (result: CrossClientQueryResult) => void) => {
  for (const cache of Object.values(state.resultsByTab)) {
    if (!cache) {
      continue
    }
    for (const page of Object.values(cache.pages)) {
      for (const result of page) {
        fn(result)
      }
    }
  }
}

export const crossClientResultsSlice = createSlice({
  name: 'crossClientResults',
  initialState,
  reducers: {
    setCrossClientResultsExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.executionLoading = action.payload
    },
    startCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = false
    },
    setCrossClientPageResults: (state, action: PayloadAction<{ tab: CrossClientResultTab; response: BasePageResponse<CrossClientQueryResult[]> }>) => {
      const { tab, response } = action.payload
      const cache = state.resultsByTab[tab] ?? { pages: {}, totalElements: 0, totalPages: 0 }
      cache.pages[response.page.number] = response.data
      cache.totalElements = response.page.totalElements
      cache.totalPages = response.page.totalPages
      state.resultsByTab[tab] = cache
    },
    setCrossClientMatchDistribution: (state, action: PayloadAction<CrossClientQueryMatchDistribution>) => {
      state.matchDistribution = action.payload
    },
    setCrossClientResultBuckets: (state, action: PayloadAction<CrossClientQueryResultBucketCount[]>) => {
      state.resultBuckets = action.payload
    },
    setCrossClientActiveEvaluations: (state, action: PayloadAction<CrossClientQueryEvaluation[]>) => {
      state.activeEvaluations = action.payload
    },
    mergeCrossClientActiveEvaluations: (state, action: PayloadAction<CrossClientQueryEvaluation[]>) => {
      const active = action.payload
      if (active.length === 0) {
        state.activeEvaluations = []
        return
      }
      const activeIds = new Set(active.map((evaluation) => evaluation.id))
      const retained = state.activeEvaluations
        .filter((evaluation) => !activeIds.has(evaluation.id))
        .map((evaluation) =>
          isTerminalEvaluationStatus(evaluation.status) ? evaluation : { ...evaluation, status: CrossClientQueryEvaluationStatuses.COMPLETED },
        )
      state.activeEvaluations = [...active, ...retained]
    },
    addCrossClientActiveEvaluation: (state, action: PayloadAction<CrossClientQueryEvaluation>) => {
      if (!state.activeEvaluations.some((evaluation) => evaluation.id === action.payload.id)) {
        state.activeEvaluations.push(action.payload)
      }
    },
    setCrossClientResultsActiveEvaluation: (state, action: PayloadAction<{ resultIds: string[]; evaluationId: string | null }>) => {
      const idSet = new Set(action.payload.resultIds)
      forEachCachedResult(state, (result) => {
        if (idSet.has(result.id)) {
          result.activeEvaluationId = action.payload.evaluationId
        }
      })
      if (action.payload.evaluationId !== null) {
        state.selectedResultIds = state.selectedResultIds.filter((id) => !idSet.has(id))
      }
    },
    setCrossClientProgressStatuses: (state, action: PayloadAction<Partial<Record<CrossClientQueryResultStatus, number>>>) => {
      state.progressStatuses = action.payload
    },
    setCrossClientResultsExecution: (state, action: PayloadAction<CrossClientQueryExecution | null>) => {
      state.execution = action.payload
    },
    setCrossClientSelectedTab: (state, action: PayloadAction<CrossClientResultTab>) => {
      state.selectedTab = action.payload
      state.paging.page = 1
      state.selectedResultIds = []
    },
    setCrossClientSearch: (state, action: PayloadAction<CrossClientResultsSearch>) => {
      state.search = action.payload
      resetResultsForFilterChange(state)
    },
    setCrossClientHasActions: (state, action: PayloadAction<boolean | null>) => {
      state.hasActions = action.payload
      resetResultsForFilterChange(state)
    },
    toggleCrossClientResultSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload
      const index = state.selectedResultIds.indexOf(id)
      if (index === -1) {
        state.selectedResultIds.push(id)
      } else {
        state.selectedResultIds.splice(index, 1)
      }
    },
    setCrossClientPageSelection: (state, action: PayloadAction<{ ids: string[]; selected: boolean }>) => {
      const { ids, selected } = action.payload
      if (selected) {
        const merged = new Set(state.selectedResultIds)
        for (const id of ids) {
          merged.add(id)
        }
        state.selectedResultIds = Array.from(merged)
      } else {
        const idSet = new Set(ids)
        state.selectedResultIds = state.selectedResultIds.filter((id) => !idSet.has(id))
      }
    },
    resetCrossClientSelection: (state) => {
      state.selectedResultIds = []
    },
    addCrossClientClients: (state, action: PayloadAction<User[]>) => {
      crossClientClientsAdapter.addMany(state.clients, action.payload)
    },
    addCrossClientFetchedClientIds: (state, action: PayloadAction<string[]>) => {
      state.fetchedClientIds = action.payload.reduce((acc, id) => ({ ...acc, [id]: true }), state.fetchedClientIds)
    },
    removeCrossClientFetchedClientIds: (state, action: PayloadAction<string[]>) => {
      const next = { ...state.fetchedClientIds }
      for (const id of action.payload) {
        delete next[id]
      }
      state.fetchedClientIds = next
    },
    setCrossClientTeammates: (state, action: PayloadAction<User[]>) => {
      state.teammates = action.payload
    },
    setCrossClientClientsLoading: (state, action: PayloadAction<boolean>) => {
      state.clientsLoading = action.payload
    },
    setCrossClientTeammatesLoading: (state, action: PayloadAction<boolean>) => {
      state.teammatesLoading = action.payload
    },
    setCrossClientPage: (state, action: PayloadAction<number>) => {
      state.paging.page = action.payload
    },
    setCrossClientPageSize: (state, action: PayloadAction<number>) => {
      state.paging.size = action.payload
      state.resultsByTab = {}
    },
    invalidateInactiveCrossClientResultTabs: (state) => {
      const active = state.resultsByTab[state.selectedTab]
      state.resultsByTab = active ? { [state.selectedTab]: active } : {}
    },
    resetCrossClientResults: () => initialState,
  },
})

export const {
  setCrossClientResultsExecutionLoading,
  startCrossClientExecutionPolling,
  stopCrossClientExecutionPolling,
  setCrossClientPageResults,
  setCrossClientMatchDistribution,
  setCrossClientResultBuckets,
  setCrossClientActiveEvaluations,
  mergeCrossClientActiveEvaluations,
  addCrossClientActiveEvaluation,
  setCrossClientResultsActiveEvaluation,
  setCrossClientProgressStatuses,
  setCrossClientResultsExecution,
  setCrossClientSelectedTab,
  setCrossClientSearch,
  setCrossClientHasActions,
  toggleCrossClientResultSelection,
  setCrossClientPageSelection,
  resetCrossClientSelection,
  addCrossClientClients,
  addCrossClientFetchedClientIds,
  removeCrossClientFetchedClientIds,
  setCrossClientTeammates,
  setCrossClientClientsLoading,
  setCrossClientTeammatesLoading,
  setCrossClientPage,
  setCrossClientPageSize,
  invalidateInactiveCrossClientResultTabs,
  resetCrossClientResults,
} = crossClientResultsSlice.actions

const crossClientClientSelectors = crossClientClientsAdapter.getSelectors((state: RootState) => state.crossClientResults.clients)

export const selectCrossClientResultsExecution = (state: RootState) => state.crossClientResults.execution

export const selectCrossClientResultExecutionLoading = (state: RootState) => state.crossClientResults.executionLoading

export const selectCrossClientSelectedTab = (state: RootState) => state.crossClientResults.selectedTab

const selectCurrentTabPage = (state: RootState): CrossClientQueryResult[] | undefined => {
  const { resultsByTab, selectedTab, paging } = state.crossClientResults
  return resultsByTab[selectedTab]?.pages[paging.page - 1]
}

export const selectCrossClientPageResults = (state: RootState) => selectCurrentTabPage(state) ?? EMPTY_RESULTS

export const selectCrossClientCurrentTabLoaded = (state: RootState) => !!selectCurrentTabPage(state)

export const selectCrossClientPageMeta = (state: RootState): PageMetadata => {
  const { resultsByTab, selectedTab, paging } = state.crossClientResults
  const cache = resultsByTab[selectedTab]
  return cache ? { totalElements: cache.totalElements, totalPages: cache.totalPages, number: paging.page - 1 } : emptyPageMeta
}

export const selectCrossClientPaging = (state: RootState) => state.crossClientResults.paging

export const selectCrossClientSearch = (state: RootState) => state.crossClientResults.search

export const selectCrossClientHasActions = (state: RootState) => state.crossClientResults.hasActions

export const selectCrossClientSelectedResultIds = (state: RootState) => state.crossClientResults.selectedResultIds

export const selectCrossClientCurrentTabUnderEvaluationCount = (state: RootState): number => {
  const { resultsByTab, selectedTab } = state.crossClientResults
  const cache = resultsByTab[selectedTab]
  if (!cache) {
    return 0
  }
  const underEvaluation = new Set<string>()
  for (const page of Object.values(cache.pages)) {
    for (const result of page) {
      if (result.activeEvaluationId) {
        underEvaluation.add(result.id)
      }
    }
  }
  return underEvaluation.size
}

export const selectCrossClientMatchDistribution = (state: RootState) => state.crossClientResults.matchDistribution

export const selectCrossClientResultBuckets = (state: RootState) => state.crossClientResults.resultBuckets ?? EMPTY_BUCKETS

export const selectCrossClientActiveEvaluations = (state: RootState) => state.crossClientResults.activeEvaluations ?? EMPTY_EVALUATIONS

export const selectCrossClientProgressStatuses = (state: RootState) => state.crossClientResults.progressStatuses

export interface CrossClientTabCounts {
  match: number
  potential: number
  remaining: number
  noMatch: number
  missingData: number
}

export const selectCrossClientTabCounts = createSelector([selectCrossClientResultBuckets], (buckets): CrossClientTabCounts => {
  const counts: CrossClientTabCounts = { match: 0, potential: 0, remaining: 0, noMatch: 0, missingData: 0 }
  for (const bucket of buckets) {
    if (bucket.status === CrossClientQueryResultStatuses.SUCCEEDED && bucket.match === CrossClientQueryResultMatches.MATCH) {
      counts.match += bucket.count
    } else if (bucket.status === CrossClientQueryResultStatuses.SUCCEEDED && bucket.match === CrossClientQueryResultMatches.NO_MATCH) {
      counts.noMatch += bucket.count
    } else if (bucket.status === CrossClientQueryResultStatuses.INITIALIZED && bucket.match === CrossClientQueryResultMatches.POTENTIAL) {
      counts.potential += bucket.count
    } else if (bucket.status === CrossClientQueryResultStatuses.INITIALIZED && bucket.match === null) {
      counts.remaining += bucket.count
    } else if (bucket.status === CrossClientQueryResultStatuses.NO_DATA) {
      counts.missingData += bucket.count
    }
  }
  return counts
})

export interface CrossClientActiveTypeProgress {
  type: CrossClientQueryEvaluationType
  evaluationIds: string[]
  processed: number
  total: number
  newMatches: number
  stillPotential: number
  missingData: number
}

export interface CrossClientActiveProgress {
  byType: CrossClientActiveTypeProgress[]
  processed: number
  total: number
}

const TYPE_ORDER: CrossClientQueryEvaluationType[] = [CrossClientQueryEvaluationTypes.AI, CrossClientQueryEvaluationTypes.LOCAL_RESOLVER]

const countProcessedAssignments = (evaluation: CrossClientQueryEvaluation): number => {
  const statusCounts = evaluation.progress?.statusCounts ?? {}
  return (
    (statusCounts[CrossClientQueryEvaluationResultStatuses.COMPLETED] ?? 0) +
    (statusCounts[CrossClientQueryEvaluationResultStatuses.FAILED] ?? 0) +
    (statusCounts[CrossClientQueryEvaluationResultStatuses.CANCELED] ?? 0)
  )
}

export const selectCrossClientActiveProgress = createSelector([selectCrossClientActiveEvaluations], (activeEvaluations): CrossClientActiveProgress => {
  const byTypeMap = new Map<CrossClientQueryEvaluationType, CrossClientActiveTypeProgress>()
  for (const evaluation of activeEvaluations) {
    const existing = byTypeMap.get(evaluation.type) ?? {
      type: evaluation.type,
      evaluationIds: [],
      processed: 0,
      total: 0,
      newMatches: 0,
      stillPotential: 0,
      missingData: 0,
    }
    const matchCounts = evaluation.progress?.producedMatchCounts ?? {}
    const matched = matchCounts[CrossClientQueryResultMatches.MATCH] ?? 0
    const potential = matchCounts[CrossClientQueryResultMatches.POTENTIAL] ?? 0
    const noMatch = matchCounts[CrossClientQueryResultMatches.NO_MATCH] ?? 0
    const completed = evaluation.progress?.statusCounts?.[CrossClientQueryEvaluationResultStatuses.COMPLETED] ?? 0
    existing.evaluationIds.push(evaluation.id)
    existing.total += evaluation.clientCount
    existing.processed += isTerminalEvaluationStatus(evaluation.status) ? evaluation.clientCount : countProcessedAssignments(evaluation)
    existing.newMatches += matched
    existing.stillPotential += potential
    existing.missingData += Math.max(completed - matched - potential - noMatch, 0)
    byTypeMap.set(evaluation.type, existing)
  }
  const byType = TYPE_ORDER.filter((type) => byTypeMap.has(type)).map((type) => byTypeMap.get(type)!)
  return {
    byType,
    processed: byType.reduce((total, group) => total + group.processed, 0),
    total: byType.reduce((total, group) => total + group.total, 0),
  }
})

export const selectCrossClientMatchedWithoutActionsCount = createSelector(
  [selectCrossClientTabCounts, selectCrossClientActionedClientIds],
  (counts, actionedClientIds) => Math.max(counts.match - actionedClientIds.size, 0),
)

export const selectCrossClientTeammates = createSelector([(state: RootState) => state.crossClientResults.teammates], (teammates) => {
  return teammates.reduce(
    (teammateMap, teammate) => {
      teammateMap[teammate.id] = teammate
      return teammateMap
    },
    {} as Record<string, User>,
  )
})

export const selectCrossClientClients = createSelector([crossClientClientSelectors.selectAll], (clients) => {
  return clients.reduce(
    (clientMap, client) => {
      clientMap[client.id] = client
      return clientMap
    },
    {} as Record<string, User>,
  )
})

export const selectCrossClientFetchedClientIds = (state: RootState) => state.crossClientResults.fetchedClientIds

export const selectCrossClientClientsLoading = (state: RootState) => state.crossClientResults.clientsLoading

export const selectCrossClientTeammatesLoading = (state: RootState) => state.crossClientResults.teammatesLoading

export const selectCrossClientResultsPollingEnabled = (state: RootState) => state.crossClientResults.pollingEnabled

export const selectShouldPollCrossClientExecutionResults = (state: RootState) => executionIsActive(state.crossClientResults.execution)

export const canLocalEvaluateForTab = (tab: CrossClientResultTab, execution: CrossClientQueryExecution | null): boolean =>
  tab === CrossClientResultTabs.POTENTIAL
    ? !!execution?.canLocalEvaluateTrue
    : tab === CrossClientResultTabs.REMAINING
      ? !!execution?.canLocalFulfillClause
      : false

export const evaluationTypeForTab = (
  tab: CrossClientResultTab,
  resolverStage: CrossClientQueryResolverStage | null | undefined,
  execution: CrossClientQueryExecution | null,
): CrossClientQueryEvaluationType => {
  if (canLocalEvaluateForTab(tab, execution) && resolverStage === CrossClientQueryResolverStages.BULK) {
    return CrossClientQueryEvaluationTypes.LOCAL_RESOLVER
  }
  return CrossClientQueryEvaluationTypes.AI
}

const outstandingTabForMatch = (match: CrossClientQueryResultMatch | null | undefined): CrossClientResultTab | null =>
  match === CrossClientQueryResultMatches.POTENTIAL
    ? CrossClientResultTabs.POTENTIAL
    : match === null || match === undefined
      ? CrossClientResultTabs.REMAINING
      : null

export const nextStepKindForResult = (result: CrossClientQueryResult, execution: CrossClientQueryExecution | null): CrossClientQueryEvaluationType | null => {
  const tab = outstandingTabForMatch(result.match)
  if (!tab) {
    return null
  }
  return evaluationTypeForTab(tab, result.lastResolverStage, execution)
}

export interface CrossClientNextStepCounts {
  localNext: number
  aiNext: number
}

export const selectCrossClientNextStepCounts = createSelector(
  [selectCrossClientResultBuckets, selectCrossClientResultsExecution, selectCrossClientSelectedTab],
  (buckets, execution, selectedTab): CrossClientNextStepCounts => {
    let localNext = 0
    let aiNext = 0
    for (const bucket of buckets) {
      if (bucket.status !== CrossClientQueryResultStatuses.INITIALIZED) {
        continue
      }
      const tab = outstandingTabForMatch(bucket.match)
      if (!tab || tab !== selectedTab) {
        continue
      }
      if (canLocalEvaluateForTab(tab, execution) && bucket.lastResolverStage === CrossClientQueryResolverStages.BULK) {
        localNext += bucket.count
      } else {
        aiNext += bucket.count
      }
    }
    return { localNext, aiNext }
  },
)

export const selectCrossClientAvailableNextStepCounts = createSelector(
  [selectCrossClientNextStepCounts, selectCrossClientActiveProgress],
  (nextStep, activeProgress): CrossClientNextStepCounts => {
    const pendingByType = new Map<CrossClientQueryEvaluationType, number>()
    for (const group of activeProgress.byType) {
      pendingByType.set(group.type, Math.max(group.total - group.processed, 0))
    }
    const localPending = pendingByType.get(CrossClientQueryEvaluationTypes.LOCAL_RESOLVER) ?? 0
    const aiPending = pendingByType.get(CrossClientQueryEvaluationTypes.AI) ?? 0
    return {
      localNext: Math.max(nextStep.localNext - localPending, 0),
      aiNext: Math.max(nextStep.aiNext - aiPending, 0),
    }
  },
)

export default crossClientResultsSlice.reducer
